import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const FadeIn = ({ children, delay = 0, className = "" }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

const ContactCard = ({ icon, title, value, href, delay }) => (
    <FadeIn delay={delay}>
        <a
            href={href}
            className="group flex items-start gap-4 p-5 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-amber-500/30 hover:-translate-y-1 transition-all duration-300"
        >
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-slate-950 transition-all duration-300 shrink-0">
                <i className={`bi ${icon} text-lg`} aria-hidden="true" />
            </div>
            <div>
                <h4 className="font-semibold text-white text-sm uppercase tracking-wider mb-1">{title}</h4>
                <p className="text-slate-400 group-hover:text-slate-300 transition-colors">{value}</p>
            </div>
        </a>
    </FadeIn>
);

export default function Contact() {
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        email: '',
        phone: '',
        service: '',
        requirements: ''
    });
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        await new Promise(resolve => setTimeout(resolve, 1500));
        setIsSubmitting(false);
        setFormSubmitted(true);
        setFormData({ name: '', company: '', email: '', phone: '', service: '', requirements: '' });
        setTimeout(() => setFormSubmitted(false), 8000);
    };

    const contactInfo = [
        { icon: 'bi-envelope', title: 'Email', value: 'smuwanguzi2@gmail.com', href: 'mailto:smuwanguzi2@gmail.com', delay: 100 },
        { icon: 'bi-telephone', title: 'Phone', value: '+256 704 698298', href: 'tel:+256704698298', delay: 200 },
        { icon: 'bi-geo-alt', title: 'Office', value: 'Kampala, Uganda', href: '#', delay: 300 },
        { icon: 'bi-clock', title: 'Working Hours', value: 'Mon - Fri: 8:00 - 18:00 EAT', href: '#', delay: 400 },
    ];

    const services = [
        "Custom Software Development",
        "SAP Business One Solutions",
        "EFRIS Integration",
        "Cloud & Infrastructure",
        "Cybersecurity",
        "IT Consultancy",
        "Other"
    ];

    return (
        <div className="bg-slate-950 min-h-screen text-white">
            {/* Hero */}
            <section className="relative py-24 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent pointer-events-none" />

                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 mb-6">
                            <i className="bi bi-chat-dots" aria-hidden="true" />
                            Get In Touch
                        </span>
                    </FadeIn>

                    <FadeIn delay={100}>
                        <h1 className="text-4xl md:text-6xl font-black font-heading leading-tight mb-6">
                            Let's Build Something
                            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                                Great Together
                            </span>
                        </h1>
                    </FadeIn>

                    <FadeIn delay={200}>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
                            Ready to integrate with EFRIS, customize SAP Business One, or build modern software?
                            Our Kampala-based team is ready to help.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-6 px-6 lg:px-12 pb-24">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16">

                    {/* Left: Contact Info */}
                    <div className="lg:col-span-2 space-y-8">
                        <FadeIn>
                            <div>
                                <h2 className="text-2xl font-bold text-white mb-2">Contact Information</h2>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Reach out directly or fill out the form. We typically respond within 24 hours.
                                </p>
                            </div>
                        </FadeIn>

                        <div className="space-y-4">
                            {contactInfo.map((item) => (
                                <ContactCard key={item.title} {...item} />
                            ))}
                        </div>

                        <FadeIn delay={500}>
                            <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800">
                                <h3 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Follow Us</h3>
                                <div className="flex gap-3">
                                    {[
                                        { icon: 'bi-linkedin', label: 'LinkedIn', href: '#' },
                                        { icon: 'bi-twitter-x', label: 'Twitter', href: '#' },
                                        { icon: 'bi-facebook', label: 'Facebook', href: '#' },
                                        { icon: 'bi-instagram', label: 'Instagram', href: '#' },
                                    ].map((social) => (
                                        <a
                                            key={social.label}
                                            href={social.href}
                                            aria-label={social.label}
                                            className="w-10 h-10 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-amber-400 hover:border-amber-500/30 hover:bg-amber-500/10 hover:-translate-y-1 transition-all duration-300"
                                        >
                                            <i className={`bi ${social.icon}`} aria-hidden="true" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </FadeIn>
                    </div>

                    {/* Right: Form */}
                    <div className="lg:col-span-3">
                        <FadeIn delay={200}>
                            <div className="relative bg-slate-900 rounded-3xl p-6 md:p-10 border border-slate-800 shadow-2xl overflow-hidden">
                                <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl" />
                                <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-blue-500/10 blur-3xl" />

                                {formSubmitted ? (
                                    <div className="relative text-center space-y-4 py-12">
                                        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center text-3xl text-emerald-400 mb-4 animate-bounce">
                                            <i className="bi bi-check-circle-fill" aria-hidden="true" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-white">Message Sent!</h3>
                                        <p className="text-slate-400 max-w-md mx-auto">
                                            Thank you for reaching out. Our team will review your request and get back to you within 24 hours.
                                        </p>
                                        <button
                                            onClick={() => setFormSubmitted(false)}
                                            className="mt-4 text-amber-400 hover:text-amber-300 text-sm font-semibold underline underline-offset-4"
                                        >
                                            Send another message
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="relative space-y-5">
                                        <div className="grid md:grid-cols-2 gap-5">
                                            <div>
                                                <label htmlFor="name" className="block text-xs uppercase tracking-widest text-slate-400 mb-2">
                                                    Full Name *
                                                </label>
                                                <input
                                                    id="name"
                                                    name="name"
                                                    type="text"
                                                    required
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    placeholder="John Doe"
                                                    className="w-full rounded-xl bg-slate-950 border border-slate-800 px-4 py-3 text-white placeholder-slate-600 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="company" className="block text-xs uppercase tracking-widest text-slate-400 mb-2">
                                                    Company Name
                                                </label>
                                                <input
                                                    id="company"
                                                    name="company"
                                                    type="text"
                                                    value={formData.company}
                                                    onChange={handleChange}
                                                    placeholder="ABC Ltd"
                                                    className="w-full rounded-xl bg-slate-950 border border-slate-800 px-4 py-3 text-white placeholder-slate-600 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-5">
                                            <div>
                                                <label htmlFor="email" className="block text-xs uppercase tracking-widest text-slate-400 mb-2">
                                                    Email Address *
                                                </label>
                                                <input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    required
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder="Email"
                                                    className="w-full rounded-xl bg-slate-950 border border-slate-800 px-4 py-3 text-white placeholder-slate-600 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="phone" className="block text-xs uppercase tracking-widest text-slate-400 mb-2">
                                                    Phone Number
                                                </label>
                                                <input
                                                    id="phone"
                                                    name="phone"
                                                    type="tel"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="+256 700 000 000"
                                                    className="w-full rounded-xl bg-slate-950 border border-slate-800 px-4 py-3 text-white placeholder-slate-600 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="service" className="block text-xs uppercase tracking-widest text-slate-400 mb-2">
                                                Service Interested In
                                            </label>
                                            <select
                                                id="service"
                                                name="service"
                                                value={formData.service}
                                                onChange={handleChange}
                                                className="w-full rounded-xl bg-slate-950 border border-slate-800 px-4 py-3 text-white focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all appearance-none cursor-pointer"
                                            >
                                                <option value="">Select a service...</option>
                                                {services.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label htmlFor="requirements" className="block text-xs uppercase tracking-widest text-slate-400 mb-2">
                                                Project Details *
                                            </label>
                                            <textarea
                                                id="requirements"
                                                name="requirements"
                                                rows={5}
                                                required
                                                value={formData.requirements}
                                                onChange={handleChange}
                                                placeholder="Tell us about your project, timeline, and goals..."
                                                className="w-full rounded-xl bg-slate-950 border border-slate-800 px-4 py-3 text-white placeholder-slate-600 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all resize-none"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className={`w-full rounded-xl py-4 font-bold text-slate-950 transition-all duration-300 flex items-center justify-center gap-2 ${isSubmitting
                                                ? 'bg-slate-700 cursor-not-allowed'
                                                : 'bg-amber-500 hover:bg-amber-400 hover:shadow-lg hover:shadow-amber-500/20'
                                                }`}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <i className="bi bi-arrow-repeat animate-spin" aria-hidden="true" />
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    Submit Request
                                                    <i className="bi bi-send-fill" aria-hidden="true" />
                                                </>
                                            )}
                                        </button>

                                        <p className="text-xs text-slate-600 text-center">
                                            By submitting this form, you agree to our{' '}
                                            <Link to="/privacy" className="text-amber-500 hover:underline">Privacy Policy</Link>
                                            {' '}and{' '}
                                            <Link to="/terms" className="text-amber-500 hover:underline">Terms of Service</Link>.
                                        </p>
                                    </form>
                                )}
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>
        </div>
    );
}